import {
  AmericanExpressIcon,
  DinnersClubInterationalIcon,
  IdealIcon,
  JcbIcon,
  MasterCardIcon,
  PaypalCreditIcon,
  PaypalWithTextIcon,
  VisaIcon,
} from '@native-instruments/webex-ni';
import clsx from 'clsx';
import { imageToSrcSet } from 'dotcom-core/src/helpers/image';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { data, useLoaderData } from 'react-router';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import type { ProductDetailPageQuery } from '~/graphql';
import { ROUTE_SEGMENTS } from '~/helpers/app-routes';
import { applyDesignSystemClasses } from '~/helpers/applyDesignSystemClasses';
import { getIsAuthenticated, verifyUserSession } from '~/helpers/auth.server';
import { makeClient } from '~/helpers/client';
import { logSentryError, mapGraphQLError } from '~/helpers/error';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';
import { getLinkedProductsForSelector, getVariant } from '~/helpers/product';
import { useCurrentUrl } from '~/hooks/useCurrentUrl';
import { useEcommerceTracking } from '~/hooks/useEcommerceTracking';
import type { Route } from '../+types';
import { AddToCartButton } from './components/AddToCartButton';
import { GiftCardForm } from './components/GiftCardForm/GiftCardForm';
import {
  PdpAudioButton,
  PdpAudioPlayer,
  usePdpAudioPlayer,
} from './components/PdpAudioPlayer';
import { PriceListing } from './components/PriceListing';
import { ProductSummary } from './components/ProductSummary/ProductSummary';
import { SystemRequirements } from './components/SystemRequirements';

export type ProductInterface = NonNullable<
  NonNullable<ProductDetailPageQuery['productDetailPage']>['product']
>;

const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  if (!data) {
    logSentryError({
      name: 'MetaFunctionError',
      message: 'Loader data is undefined in meta function',
      source: 'server',
    });
    return [{ title: '' }, { name: 'description', content: '' }];
  }
  const rootMeta = matches.find((match) => match.id === 'root')?.meta ?? [];
  return [
    ...rootMeta,
    { title: data.page.seoTitle },
    { name: 'description', content: data.page.seoDescription },
  ];
};

async function loader(loaderArgs: LoaderFunctionArgs) {
  const { params, request } = loaderArgs;
  const { language, slug = '' } = params;
  const client = makeClient(loaderArgs);
  const headers = new Headers();

  const { session } = await verifyUserSession(request, headers);

  headers.append(
    'Cache-Control',
    getIsAuthenticated(loaderArgs.request) ? 'private' : 'public',
  );

  try {
    const { productDetailPage } = await client.ProductDetailPage({ slug });

    if (!isTruthy(productDetailPage)) {
      throw new Error(`ProductDetailPage not found for slug: ${slug}.`);
    }

    if (!isTruthy(productDetailPage?.product)) {
      throw new Error(`Product not found for slug: ${slug}.`);
    }

    const { addressCountry: countryRelatedInfo } = await client.Country();

    const linkedProducts = getLinkedProductsForSelector(
      { ...productDetailPage.product, slug: productDetailPage.slug },
      productDetailPage.linkedProductDetailPages?.map(({ slug, product }) => ({
        slug,
        product: product as ProductInterface,
      })),
    );

    const variant = getVariant(productDetailPage.product);
    const addToCartSku = isTruthy(variant)
      ? variant.sku
      : productDetailPage.product.sku;

    return data(
      {
        language,
        page: productDetailPage,
        product: productDetailPage.product,
        variant,
        linkedProducts,
        addToCartSku,
        countryRelatedInfo,
        showRetailerLink: productDetailPage.showRetailerLink,
        user: session.get('user'),
      },
      {
        headers,
      },
    );
  } catch (error) {
    const errorMessage = mapGraphQLError({
      error,
      operationName: 'ProductDetailPage',
      extra: { slug },
    });

    throw new Error(errorMessage);
  }
}

function ProductDetail() {
  const { t } = useIntl();
  const { page, product, variant, language, addToCartSku, user } =
    useLoaderData<typeof loader>();
  const { currentUrl } = useCurrentUrl();

  const {
    playerRef,
    isPlayerPlaying,
    isPlayerInitialized,
    handleTogglePlayer,
    handlePlayStateChange,
    handlePlayerClose,
    playlist,
  } = usePdpAudioPlayer(product.audios);

  useEcommerceTracking({
    event: 'view_item',
    customerType: user?.ecommerceStatus,
    product,
    variant,
  });

  const productImage = product?.images?.[0];

  const breadcrumbs = [
    { name: 'Home', path: `/${language}/` },
    { name: 'Shop', path: `/${language}/${ROUTE_SEGMENTS.catalog}` },
    {
      name: page.product.name,
      path: currentUrl,
    },
  ];

  // @TODO uncomment once BE is ready: [WWW-12584]
  // const isGiftCardProduct = product.__typename === 'GiftCardProduct';
  const isGiftCardProduct = false;

  return (
    <>
      <div className="container grid-cols-4 webex-grid lg:grid-cols-8 xl:grid-cols-12 pb-4">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <div className="col-span-4 lg:col-span-5 xl:col-span-8">
          <div className="aspect-[720/405] mb-6 bg-grey-20 rounded overflow-hidden">
            {isTruthy(productImage) ? (
              <picture>
                {imageToSrcSet(productImage).map((image) => (
                  <source
                    key={image?.variationName}
                    media={`(min-width: ${image?.size})`}
                    srcSet={image.srcSet}
                    type="image/webp"
                  />
                ))}

                <img
                  width="100%"
                  loading="lazy"
                  src={productImage?.uri ?? ''}
                  alt={productImage?.alternativeText ?? `${product.name} Image`}
                  data-testid="product-header-image"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = '';
                  }}
                />
              </picture>
            ) : (
              <div className="bg-[#ddd] w-full h-full flex justify-center items-center" />
            )}
          </div>

          <div
            className="w-full lg:hidden"
            data-testid="product-summary-mobile"
          >
            <ProductSummary />
          </div>

          {isGiftCardProduct && <GiftCardForm />}

          <Overview />

          {isTruthy(product.audios?.length) && (
            <PdpAudioButton
              title={product.name}
              isPlaying={isPlayerPlaying}
              onClick={handleTogglePlayer}
            />
          )}

          <AdditionalInformation />

          <ProductIncludes />

          <SystemRequirements requirements={product.systemRequirements ?? []} />
        </div>
        <div
          className="hidden self-start col-span-4 sticky top-[20px] lg:block lg:col-span-3 xl:col-span-4"
          data-testid="product-summary-desktop"
        >
          <ProductSummary />
        </div>
      </div>

      {/* Sticky PriceListing and Add to cart button, only for mobile */}
      <div
        className={clsx(
          'lg:hidden sticky p-3 bottom-0 space-y-3 left-0 bg-white border-t border-grey-40',
          { 'pb-10': isPlayerPlaying },
        )}
      >
        <PriceListing product={product} variant={variant} />

        <AddToCartButton
          product={product}
          variant={variant}
          data-testid={`add-to-cart-mobile-${addToCartSku}`}
        />
      </div>

      <div className="container my-6 lg:my-12">
        <div
          className="my-3 flex gap-x-3 gap-y-3 md:gap-x-5 justify-center flex-wrap"
          data-testid="payments-list"
        >
          <PaypalWithTextIcon />
          <PaypalCreditIcon width="95" />
          <MasterCardIcon />
          <VisaIcon />
          <AmericanExpressIcon />
          <DinnersClubInterationalIcon />
          <JcbIcon />
          <IdealIcon />
        </div>

        <div className="text-2xs font-normal text-center">
          <span>
            {t('paymentMethods.note', {
              fallback:
                'Some of these payment methods might not be supported in your country.',
            })}
          </span>
          &nbsp;
          <a
            className="text-2xs underline font-medium"
            href="https://www.native-instruments.com/en/shop-information/shipping-fees-payment/"
          >
            {t('common.buttons.learnMore', {
              fallback: 'Learn more.',
            })}
          </a>
        </div>
      </div>
      {isPlayerInitialized && (
        <div className="sticky bottom-0 left-0 w-full z-50">
          <PdpAudioPlayer
            ref={playerRef}
            playlist={playlist}
            autoPlay={false}
            onPlayStateChange={handlePlayStateChange}
            onClose={handlePlayerClose}
          />
        </div>
      )}
    </>
  );
}

const Overview = () => {
  const { product } = useLoaderData<typeof loader>();
  const { t } = useIntl();

  if (!isTruthy(product.overview?.trim())) return null;

  return (
    <div className="mt-6 xl:mt-8">
      <h5 className="mb-1 webex-h3" data-testid="overview">
        {t('pricing.overview.title', {
          fallback: 'Overview',
        })}
      </h5>

      <div
        className="w-full"
        dangerouslySetInnerHTML={{
          __html: applyDesignSystemClasses({
            html: product.overview ?? '',
            designSystemRules: [{ tag: 'ul', className: 'webex-list-arrow' }],
          }),
        }}
      />
    </div>
  );
};

const AdditionalInformation = () => {
  const { product } = useLoaderData<typeof loader>();
  const { t } = useIntl();

  if (!isTruthy(product.additionalInformation)) return null;

  return (
    <div className="mt-6 xl:mt-8">
      <h5 className="mb-1 webex-h3" data-testid="additional-information">
        {t('pricing.additionalInformation.title', {
          fallback: 'Additional information',
        })}
      </h5>

      <div
        className="w-full"
        dangerouslySetInnerHTML={{
          __html: applyDesignSystemClasses({
            html: product.additionalInformation,
          }),
        }}
      />
    </div>
  );
};

const ProductIncludes = () => {
  const { t } = useIntl();
  const { product } = useLoaderData<typeof loader>();

  const html = product.whatIsIncluded;

  if (!isTruthy(html)) return null;

  const content: string[] = Array.isArray(html) ? html : Array(html);

  return (
    <div className="mt-6 xl:mt-8">
      <h5 className="mb-1 webex-h3" data-testid="what-you-get-title">
        {t('pricing.productIncludes.title', { fallback: 'Product includes' })}
      </h5>

      <ul className="webex-list-disc">
        {content.map((feature, i) => (
          <li
            key={i}
            dangerouslySetInnerHTML={{
              __html: applyDesignSystemClasses({ html: feature }),
            }}
          />
        ))}
      </ul>
    </div>
  );
};

const headers = ({ loaderHeaders }: Route.HeadersArgs) => {
  return { 'Cache-Control': loaderHeaders.get('Cache-Control') };
};

export { meta, loader, headers };

export default ProductDetail;
